/**
 * IapFlow — 결제(IAP) 흐름: Provider 결선 + 광고 제거 구매 + entitlement 동기화 (S5, Phaser-free).
 *
 * GameScene 추출: Provider(DIP — 네이티브 스토어 결제 Provider) 생성과
 * adFree entitlement의 읽기/쓰기·저장·UI 무효화를 셸 콜백으로 주입받아 수행한다.
 * adFree 소유는 progress(셸) — flow는 게이트/백필만 (중복 지급 없음: boolean idempotent).
 */
import { Capacitor } from "@capacitor/core";
import { createPaymentProvider } from "../payment/createPaymentProvider.js";
import { StoreProductCatalog, type PaymentProvider } from "@yjsgames/payment-ports";
import {
  IAP_PRODUCTS,
  REMOVE_ADS_PRODUCT_ID,
  REMOVE_ADS_PACKAGE_PRODUCT_ID,
  bonusDiamondOf,
  grantsAdFree,
  iapProductById,
  nativeStoreProductIdOf,
} from "../data/iapData.js";
import { BigNumber } from "@yjsgames/bignumber";
import type { AnalyticsSink } from "@yjsgames/analytics-ports";
import type { AnalyticsMilestoneController } from "../systems/AnalyticsMilestoneController.js";
import { trackFirstPurchase } from "../analytics/trackFirstPurchase.js";
import type { EconomyController } from "../systems/EconomyController.js";
import { t } from "../i18n/i18n.js";
import { diag } from "./diag.js";

const log = diag("iap");
const STORE_PRODUCT_PENDING_LABEL = "준비중..";
const NATIVE_IAP_BOOT_SYNC_DELAY_MS = 4000;
const NATIVE_IAP_OPERATION_TIMEOUT_MS = 2500;

export interface IapFlowDeps {
  analytics: () => AnalyticsSink;
  /** 분석 마일스톤 — 첫 구매(first_purchase) 1회 dedup (상자/일일상점과 공용). */
  analyticsMilestones: () => AnalyticsMilestoneController;
  /** economy getter — applySaveData 교체 stale 방지. */
  economy: () => EconomyController;
  adFree: () => boolean;
  setAdFree: (value: boolean) => void;
  /** 결과 문구 표시 (상점 하단 라벨) — 이미 현지화된 문자열. */
  setBoxResult: (message: string) => void;
  invalidateUi: () => void;
  /** 즉시 저장 (debounce 우회) — 매출 직결 분기. */
  persistNow: () => void;
  /**
   * adFree 전환(구매 성공/복원 백필) 직후 콜백 — 모바일 배너 제거 + 화면 비율 전체화면 재적용(reload)
   * 결선용. 선택(테스트/웹 미주입 안전). adFree=true로 바뀐 직후에만 호출(idempotent boolean).
   */
  onAdFreeChanged?: () => void;
  /** 외부 스토어 처리 시작/종료 overlay 결선. 미주입 시 동작 변화 없음. */
  onStoreProcessingStart?: () => void;
  onStoreProcessingEnd?: () => void;
  /** 네이티브 IAP 부팅 복원 지연. 배너 native bridge 호출이 먼저 지나가도록 둔다. */
  nativeIapBootSyncDelayMs?: number;
  /** 네이티브 IAP 조회/복원 timeout. 에뮬레이터/미지원 Billing 환경에서 UI를 막지 않는다. */
  nativeIapOperationTimeoutMs?: number;
}

export class IapFlow {
  private provider: PaymentProvider;
  private readonly storeProducts = new StoreProductCatalog(
    IAP_PRODUCTS.map((p) => ({
      productId: p.productId,
      storeProductId: nativeStoreProductIdOf(p.productId),
    })),
  );
  private readonly isNative = Capacitor.isNativePlatform();
  private productRefreshRequested = false;

  constructor(private readonly deps: IapFlowDeps) {
    // 결제 Provider (DIP) — 네이티브 Provider가 상품 조회/구매를 담당. 폴백 Provider는 IAP 상품을 노출하지 않는다.
    this.provider = createPaymentProvider({
      analytics: deps.analytics(),
      economyRef: () => deps.economy(),
      diamondPriceOf: () => 0,
      onNativeProvider: (p) => {
        this.provider = p;
        this.productRefreshRequested = false;
        this.scheduleNativeEntitlementSync();
      },
    });
  }

  /** 광고 제거(단독) 구매. (dev api buyRemoveAds — 상점 카드/CheatModal 경로) */
  buyRemoveAds(): void {
    this.buyAdFreeProduct(REMOVE_ADS_PRODUCT_ID);
  }

  /** 광고 제거 + 다이아 패키지 구매 — 성공 시 보너스 다이아(bonusDiamond)도 1회 지급. (dev api buyRemoveAdsPackage) */
  buyRemoveAdsPackage(): void {
    this.buyAdFreeProduct(REMOVE_ADS_PACKAGE_PRODUCT_ID);
  }

  /** 스토어에서 정확히 조회된 상품만 상점 UI에 노출한다. */
  isProductAvailable(productId: string): boolean {
    this.requestProductRefresh();
    return this.storeProducts.isAvailable(productId);
  }

  /** 네이티브 인앱 API가 반환한 현지화 가격 라벨. 상품 미조회 시 undefined. */
  productPriceLabel(productId: string): string | undefined {
    this.requestProductRefresh();
    return this.storeProducts.priceLabel(productId);
  }

  private requestProductRefresh(): void {
    if (!this.isNative || this.productRefreshRequested) return;
    this.productRefreshRequested = true;
    void this.refreshProductAvailability();
  }

  private async refreshProductAvailability(): Promise<void> {
    const changed = await this.withNativeIapTimeout(
      this.storeProducts.refresh(this.provider),
      false,
      "product availability refresh",
    );
    if (changed) this.deps.invalidateUi();
  }

  /**
   * 광고 제거 entitlement 상품 구매 (단독/패키지 공용) — 성공 시 adFree=true 저장 → ad 슬롯 즉시 수령
   * 전환(+배너/리워드 차단). 패키지면 bonusDiamond를 1회 지급(결제 트랜잭션에서만 — 복원 백필은 재지급 안 함).
   * 상호배타: 이미 adFree면 어느 상품도 구매하지 않는다(하나 보유 시 다른 하나 못 삼). 취소는 무시한다.
   */
  private buyAdFreeProduct(productId: string): void {
    if (this.deps.adFree()) return; // 상호배타 — 이미 광고제거 보유면 단독·패키지 둘 다 차단
    if (!this.isProductAvailable(productId)) {
      this.deps.setBoxResult(STORE_PRODUCT_PENDING_LABEL);
      this.deps.invalidateUi();
      return;
    }
    const storeProductId = this.storeProducts.storeProductIdOf(productId);
    log.diag("buyAdFreeProduct", `구매 시도 ${productId} store=${storeProductId}`);
    this.deps.onStoreProcessingStart?.();
    try {
      this.provider.purchase({
        productId,
        storeProductId,
        kind: "nonConsumable",
        onSuccess: () => {
          this.deps.onStoreProcessingEnd?.();
          this.deps.setAdFree(true);
          const bonus = bonusDiamondOf(productId);
          if (bonus > 0) this.deps.economy().addDiamond(BigNumber.fromNumber(bonus)); // 패키지 보너스 — 결제 1회만 지급
          this.deps.analytics().log("purchase", { type: productId });
          trackFirstPurchase(this.deps.analytics(), this.deps.analyticsMilestones()); // 첫 구매 1회 퍼널 (funnel #9)
          this.deps.setBoxResult(t(`${this.descPrefix(productId)}.success`));
          this.deps.invalidateUi();
          this.deps.persistNow(); // 결제 성공 — 즉시 저장(매출 직결, 유실 불가)
          this.deps.onAdFreeChanged?.(); // 배너 제거 + 전체화면 재적용 (구매 즉시 reload)
        },
        onCancel: () => {
          this.deps.onStoreProcessingEnd?.();
          /* 사용자 취소 — 토스트 없음 */
        },
        onError: (e) => {
          this.deps.onStoreProcessingEnd?.();
          log.diag("buyAdFreeProduct", `실패 ${productId} code=${e.code}`);
          this.deps.setBoxResult(t(`${this.descPrefix(productId)}.failed`));
          this.deps.invalidateUi();
        },
      });
    } catch (error) {
      this.deps.onStoreProcessingEnd?.();
      throw error;
    }
  }

  /** 상품 i18n 키 prefix — descKey에서 ".desc"를 떼어 ".success"/".failed" 공용 prefix를 얻는다(데이터 파생). */
  private descPrefix(productId: string): string {
    return iapProductById(productId)?.descKey.replace(/\.desc$/, "") ?? "iap.removeAds";
  }

  /**
   * IAP 보유(restorePurchases)와 adFree 캐시 동기화 — 부팅/네이티브 Provider 교체 시 호출.
   * 보유에 광고제거 상품(단독 또는 패키지)이 있으면 adFree 백필(idempotent). 복원 실패는 로컬 신뢰.
   * 패키지 보너스 다이아는 결제 트랜잭션에서만 지급하므로 여기서는 재지급하지 않는다(중복 방지).
   */
  async syncEntitlements(): Promise<void> {
    try {
      const owned = await this.withNativeIapTimeout(
        this.provider.restorePurchases(),
        [] as readonly string[],
        "entitlement restore",
      );
      if (owned.some(grantsAdFree) && !this.deps.adFree()) {
        this.deps.setAdFree(true);
        this.deps.persistNow(); // entitlement 복원 백필 — 즉시 저장(매출 직결)
        this.deps.invalidateUi();
        this.deps.onAdFreeChanged?.(); // 복원 백필 — 배너 제거 + 전체화면 재적용
      }
    } catch {
      /* 복원 실패 — 로컬 adFree 신뢰 */
    }
  }

  private scheduleNativeEntitlementSync(): void {
    if (!this.isNative) {
      void this.syncEntitlements();
      return;
    }
    const delayMs = this.deps.nativeIapBootSyncDelayMs ?? NATIVE_IAP_BOOT_SYNC_DELAY_MS;
    setTimeout(() => {
      void this.syncEntitlements();
    }, delayMs);
  }

  private async withNativeIapTimeout<T>(promise: Promise<T>, fallback: T, label: string): Promise<T> {
    if (!this.isNative) return promise;
    const timeoutMs = this.deps.nativeIapOperationTimeoutMs ?? NATIVE_IAP_OPERATION_TIMEOUT_MS;
    try {
      return await withTimeout(promise, timeoutMs, `native IAP ${label} timed out`);
    } catch (error) {
      console.warn(`[IapFlow] ${label} skipped: ${(error as Error).message}`);
      return fallback;
    }
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}
